import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";

interface Allergy {
  id?: string;
  allergen: string;
  severity: string;
}

interface AllergiesCardProps {
  allergies: Allergy[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof Allergy, value: string) => void;
}

export function AllergiesCard({ allergies, onAdd, onRemove, onUpdate }: AllergiesCardProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "sévère":
        return "bg-red-100 text-red-700 border-red-200";
      case "modérée":
        return "bg-orange-100 text-orange-700 border-orange-200";
      default:
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
    }
  };

  return (
    <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Allergies</CardTitle>
              <CardDescription>{allergies.length} allergie(s) connue(s)</CardDescription>
            </div>
          </div>
          <Button onClick={onAdd} variant="outline" size="sm" className="gap-2">
            <Plus className="w-4 h-4" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {allergies.length === 0 ? (
          <div className="text-center py-8 bg-muted/30 rounded-xl">
            <AlertTriangle className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">Aucune allergie enregistrée</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allergies.map((allergy, index) => (
              <div 
                key={index} 
                className="p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 rounded-xl border border-red-100 dark:border-red-900"
              >
                <div className="flex justify-between items-start mb-3">
                  <Badge className={`${getSeverityColor(allergy.severity)} border`}>
                    {allergy.severity || "Sévérité non définie"}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="h-8 w-8 p-0">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Allergène</Label>
                    <Input
                      value={allergy.allergen}
                      onChange={(e) => onUpdate(index, "allergen", e.target.value)}
                      placeholder="Pollen, Arachides..."
                      className="h-9 bg-white dark:bg-background"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Sévérité</Label>
                    <Select
                      value={allergy.severity}
                      onValueChange={(value) => onUpdate(index, "severity", value)}
                    >
                      <SelectTrigger className="h-9 bg-white dark:bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mineure">Mineure</SelectItem>
                        <SelectItem value="modérée">Modérée</SelectItem>
                        <SelectItem value="sévère">Sévère</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
